/*
 * This file is part of the Convergence API Server.
 *
 * (c) Convergence <https://convergence.finance/>
 */

import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import React from "react";
import { withApollo, WithApolloClient } from "react-apollo";
import { CREATE_CONVERSATION } from "../../../../graphql/mutation/ConversationMutation";
import { SEARCH_DEALS_QUERY } from "../../../../graphql/query/DealQuery";
import ValidationService from "../../../../service/ValidationService";
import { processError } from "../../../../utils/CommonUtils";
import Link from "../../../theme/3.0/Link";
import Loader from "../../../layout/Loader";
import Modal from "../../../layout/Modal/Modal";
import ModalCard from "../../../layout/Modal/ModalCard";
import ModalCardHeader from "../../../layout/Modal/ModalCardHeader";
import SendMessageMessageForm from "./SendMessageMessageForm";
import SendMessageSelectRecipientsForm from "./SendMessageSelectRecipientsForm";

type Props = {
    deal;
    selectRecipients;
    institution;
    toggle;
};

type State = {
    step: string;
    error: string;
    success: boolean | string;
    loading: boolean;
    form: {
        message: string;
        related_deal: any; // TS TODO TYPE STRICTER
        recipients: string[];
    };
    isValid: boolean;
    validation; // TS HACK TODO: ValidationService.validations()
};
class SendMessageModal extends React.Component<WithApolloClient<Props>, State> {
    // TS CODE CHANGE. MOVED TO STATIC PROP TYPES
    static propTypes = {
        institution: PropTypes.object.isRequired,
        deal: PropTypes.object,
        selectRecipients: PropTypes.bool
    };
    validationService: ValidationService;
    debounceGetDeals: any;
    constructor(props) {
        super(props);

        this.validationService = new ValidationService(this.validationRules());

        this.state = {
            step:
                !this.props.deal || this.props.selectRecipients ? "ONE" : "END",
            error: "",
            success: "",
            loading: false,
            form: {
                message: "",
                related_deal: "",
                recipients: []
            },
            isValid: true,
            validation: this.validationService.getValidations()
        };
        this.debounceGetDeals = debounce(this.getDeals, 500, {
            leading: false,
            trailing: true
        });
    }

    validationRules = () => ({
        message: {
            notEmpty: {
                message: "You must enter a message"
            }
        },
        recipients: {
            hasLength: {
                enabled: () => this.state.step === "TWO",
                minLength: 1,
                maxLength: 2,
                message: (validationDate, checkValue) => {
                    if (checkValue.length > validationDate.maxLength) {
                        return "You can only select a maximum of two (2) recipients.";
                    }

                    if (checkValue.length < validationDate.minLength) {
                        return "You must select at least one (1) recipient.";
                    }
                }
            }
        }
    });

    onSubmit = (event) => {
        event.preventDefault();

        const { client } = this.props;
        const { form, step } = this.state;

        this.validationService.validateFields(form);

        if (this.validationService.isValid) {
            if (step === "ONE") {
                this.setState({ step: "TWO" });
            }

            if (step === "TWO" || step === "END") {
                this.setState({ loading: true });

                let input: {
                    to_institution_id;
                    message;
                    deal_id?;
                    recipients?;
                } = {
                    to_institution_id: this.props.institution.id,
                    message: form.message
                };

                if (this.props.deal) {
                    input.deal_id = this.props.deal.id;
                } else if (form.related_deal && form.related_deal.value) {
                    input.deal_id = form.related_deal.value;
                }

                if (form.recipients.length > 0) {
                    input.recipients = form.recipients;
                }

                client
                    .mutate({
                        mutation: CREATE_CONVERSATION,
                        variables: {
                            input
                        }
                    })
                    .then(() => {
                        this.setState({ success: true, loading: false });
                    })
                    .catch((error) => {
                        this.setState({
                            error: processError(error),
                            loading: false
                        });
                    });
            }
        }

        this.setState({ isValid: this.validationService.isValid });
    };

    onChange = (event) => {
        const { form } = this.state;
        const fieldName = event.target.name;
        const fieldValue = event.target.value;

        this.validationService.setData(form);
        this.validationService.validateField(fieldName, fieldValue);

        this.setState(({ form }) => ({
            form: {
                ...form,
                [fieldName]: fieldValue
            },
            validation: this.validationService.getValidations(),
            isValid: this.validationService.isValid
        }));
    };

    onChangeDeal = (related_deal) => {
        this.setState(({ form }) => ({
            form: {
                ...form,
                related_deal
            }
        }));
    };

    selectParticipant = (participant_id) => {
        let { recipients } = this.state.form;
        const findIndex = recipients.indexOf(participant_id);

        if (findIndex === -1) {
            recipients.push(participant_id);
        } else {
            recipients.splice(findIndex, 1);
        }

        this.validationService.setData(this.state.form);
        this.validationService.validateField("recipients", recipients);

        this.setState(({ form }) => ({
            form: {
                ...form,
                recipients
            },
            validation: this.validationService.getValidations(),
            isValid: this.validationService.isValid
        }));
    };

    getDeals = (keyword, callback) => {
        if (!keyword) {
            callback(null, {
                options: [],
                complete: true
            });
        } else {
            const { client } = this.props;

            client
                .query({
                    query: SEARCH_DEALS_QUERY,
                    variables: {
                        keyword
                    },
                    fetchPolicy: "network-only"
                })
                .then((results) => {
                    const { data } = results;
                    if (data && data.deals) {
                        let deals = data.deals.map((deal) => ({
                            value: deal.id,
                            label: deal.name
                        }));
                        callback(deals);
                    } else {
                        callback([]);
                    }
                });
        }
    };

    render() {
        const {
            form,
            step,
            error,
            success,
            loading,
            validation,
            isValid
        } = this.state;
        const { institution, deal } = this.props;

        return (
            <Modal hasBackground={true} isActive={true} centered={true}>
                <form onSubmit={this.onSubmit}>
                    <ModalCard>
                        <ModalCardHeader
                            onDismiss={this.props.toggle}
                            title="New Message"
                        />
                        {loading && (
                            <div className={"text-center m-3"}>
                                <Loader />
                            </div>
                        )}
                        {error && (
                            <div className={"alert alert-danger m-3"}>
                                <h4>An error occurred</h4>
                                <p className={"mb-0"}>{error}</p>
                            </div>
                        )}
                        {success && (
                            <div className={"alert alert-success m-3"}>
                                <h4>Message Sent</h4>
                                <p className={"mb-0"}>
                                    Your message to{" "}
                                    {this.props.institution.name} was sent. You
                                    can view this thread and others in{" "}
                                    <Link
                                        label={"messages"}
                                        routeKey={"institution-messages"}
                                    />
                                </p>
                            </div>
                        )}
                        {!loading && !error && !success && (
                            <React.Fragment>
                                {(step === "ONE" || step === "END") && (
                                    <SendMessageMessageForm
                                        form={form}
                                        institution={institution}
                                        validation={validation}
                                        isValid={isValid}
                                        onChange={this.onChange}
                                        onChangeDeal={this.onChangeDeal}
                                        deal={deal}
                                        step={step}
                                        getDeals={this.debounceGetDeals}
                                    />
                                )}
                                {step === "TWO" && (
                                    <SendMessageSelectRecipientsForm
                                        form={form}
                                        institution={institution}
                                        validation={validation}
                                        isValid={isValid}
                                        onChange={this.onChange}
                                        deal={deal}
                                        step={step}
                                        selectParticipant={
                                            this.selectParticipant
                                        }
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </ModalCard>
                </form>
            </Modal>
        );
    }
}

export default withApollo<Props>(SendMessageModal);
